import { Location } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { ActivatedRoute, Router } from '@angular/router';

import { environment } from '../../../../environments/environment';
import { ConfirmationDialogData } from '../../../models/confirmation-dialog-data.dto';
import { InspectionCrudService } from '../../../services/api/inspection-crud/inspection-crud.service';
import { InspectionService } from '../../../services/api/inspection/inspection.service';
import { CubeService, MessageType } from '../../../services/cube/cube.service';
import { LoaderService } from '../../../services/loader/loader.service';
import {
  IdbService,
  WhereConditions,
} from '../../../services/storage/idb.service';
import {
  StorageService,
  STORAGE_KEYS,
} from '../../../services/storage/storage.service';
import { TemplateLoaderService } from '../../../services/template-loader/template-loader.service';
import { UserService } from '../../../services/user/user.service';
import { InspectionStatus } from '../../../shared/configs/inspection-status.config';
import { InspectionType } from '../../../shared/configs/inspection-type.config';
import { StructureType } from '../../../shared/configs/structure-type.config';
import { TunnelConstants } from '../../../shared/configs/tunnel-constants.config';
import { TunnelTemplateVersion } from '../../../shared/configs/tunnel-template-version.config';
import { UserRole } from '../../../shared/configs/user-role.config';

import { FormlyFormOptions } from '@ngx-formly/core';
import { Subscription } from 'dexie';
import { from, Subject } from 'rxjs';
import { filter, map, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'cube-tunnel-input-template',
  templateUrl: './tunnel-input-template.component.html',
  styleUrls: ['./tunnel-input-template.component.scss'],
  providers: [
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: { appearance: 'fill' },
    },
  ],
  standalone: false,
})
export class TunnelInputTemplateComponent implements OnInit, OnDestroy {
  concioCount: number = 0;
  concioNoList: any[] = [];
  defectUpdateStatus: boolean = false;
  devMode = environment.devMode; // TEMP: For development
  fields: any[] = [];
  form: FormGroup = new FormGroup({});
  l0BackupData: any = {};
  l1BackupData: any = {};
  l1InitBackupData: any = {};
  l2UpdateStatus: boolean = false;
  levelsList: string[];
  model: any;
  options: FormlyFormOptions;
  token: string;
  tunnelConstants: typeof TunnelConstants = TunnelConstants;

  private defectImageListForModification: any[] = [];
  private defectUpdateIndex: number;
  private destroy$: Subject<void> = new Subject<void>();
  private appImportedInspectionId: string = '';
  private inspectionId: string = '';
  private inspectionType: number = 0;
  private isDisabled: boolean = false;
  private isOnline: boolean = true;
  private l1UpdateIndex: number;
  private l1UpdateStatus: boolean = false;
  private l2ConcioNo: number;
  private l2UpdateIndex: number;
  private migrationDelay: number = 2500; // TODO
  private idbRecordId: string;
  private plannedInspection;
  private plannedInspectionId: string = '';
  private recordId: string = '';
  private repeatSectionImageListForModification: any[] = [];
  private saveStatus: boolean = false;
  private templateVersion: TunnelTemplateVersion;
  private tenantId: string;
  private unsubscribe$: Subscription;

  constructor(
    private activatedRoute: ActivatedRoute,
    private cubeService: CubeService,
    private idbService: IdbService,
    private inspectionCrudService: InspectionCrudService,
    private inspectionService: InspectionService,
    private loaderService: LoaderService,
    private location: Location,
    private router: Router,
    private storageService: StorageService,
    private templateLoaderService: TemplateLoaderService,
    private userService: UserService
  ) {
    this.recordId = this.activatedRoute.snapshot.paramMap.get('recordId');
    this.inspectionType = +this.activatedRoute.snapshot.paramMap.get('type');
    this.templateVersion = this.activatedRoute.snapshot.paramMap.get(
      'templateVersion'
    ) as TunnelTemplateVersion;
    this.tenantId = this.activatedRoute.snapshot.paramMap.get('tenantId');
    this.storageService.addToStorage(STORAGE_KEYS.savedImages, []);
    this.storageService.addToStorage(STORAGE_KEYS.deletedFiles, []);
    this.levelsList = this.createLevelsList();
    this.model = this.createModel();
    this.options = this.createFormlyOptions();
  }

  async ngOnInit(): Promise<void> {
    await this.setIsOnline();
    if (this.isOnline) {
      this.getInspectionMediaToken().then(() => {
        this.generateTemplate();
      });
    } else {
      this.token = this.storageService.getFromStorage(
        STORAGE_KEYS.inspectionToken
      );
      this.generateTemplate();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$?.unsubscribe();
    this.clearEmptyModel();
    this.destroy$.next();
    this.destroy$.complete();
  }

  addDefectImagesIndexToBeModified(defectList: any[], index: number): void {
    for (let i = index; i < defectList.length; i++) {
      const defectTableList =
        defectList[i].modelstructure['Scheda Ispezione Ponti di Livello 1'][
          'Scheda Ispezione Ponti di Livello 1 List1'
        ];
      let indexUpdatedFileList = [];
      for (const defectTableListItem of defectTableList) {
        if (defectTableListItem['L']) {
          const fileUrls = [].concat(
            Object.keys(defectTableListItem['L']['sezioneLongitudinale1']),
            Object.keys(defectTableListItem['L']['sezioneLongitudinale2']),
            Object.keys(defectTableListItem['L']['sezioneLongitudinale3']),
            Object.keys(defectTableListItem['L']['sezioneLongitudinale4'])
          );
          indexUpdatedFileList = indexUpdatedFileList.concat(
            fileUrls
              .map((fileUrl) => {
                if (!this.cubeService.isAlreadyUploadedFile(fileUrl))
                  return {
                    fileName: fileUrl,
                    newIndex: i,
                  };
              })
              .filter((res) => res)
          );
        }
      }
      this.defectImageListForModification =
        this.defectImageListForModification.concat(indexUpdatedFileList);
    }
  }

  addRepeatSectionImagesIndexToBeModified(
    list: any[],
    index: number,
    key: string
  ): void {
    for (let i = index; i < list.length; i++) {
      if (list[i][key]) {
        const fileAlreadyInListIndex =
          this.repeatSectionImageListForModification.findIndex(
            ({ fileName }) => fileName === list[i][key]
          );

        if (fileAlreadyInListIndex !== -1)
          this.repeatSectionImageListForModification[fileAlreadyInListIndex][
            'newIndex'
          ] = i;
        else
          this.repeatSectionImageListForModification.push({
            fileName: list[i][key],
            newIndex: i,
          });
      }
    }
  }

  // TODO: needed after the temp table change?
  @HostListener('window:beforeunload')
  clearEmptyModel(): void {
    // If user navigates away from page before/without saving
    if (
      !this.saveStatus &&
      this.inspectionType === InspectionType.newInspection
    )
      // Delete all files in indexed db under this inspection
      this.idbService.deleteAllUploadedFilesUnderInspection(
        this.model.modelHash,
        this.model.structure_type
      );
  }

  deleteUploadedDefectFiles(urlList: string[]): void {
    // INFO: Used in template
    // Deleting each defect file
    const deletedFilesList = urlList.map(([fotoKey, foto]) => ({
      fileName: this.cubeService.isAlreadyUploadedFile(foto)
        ? foto // File is saved in DB
        : fotoKey, // File is saved in indexedDB
      isDefect: true,
    }));

    this.storageService.addDeletedFileUrlsToStorage(deletedFilesList);
  }

  deleteUploadedFiles(fileNames: string | string[]): void {
    // INFO: Used in template
    if (!fileNames.length) return;

    // Adding files to deleted files list in local storage
    let deletedFileUrlsList: any[];
    if (Array.isArray(fileNames)) {
      // fileNames is a list of file names
      deletedFileUrlsList = fileNames.map((fileName) => ({ fileName }));
    } else {
      // fileNames is a single file name
      deletedFileUrlsList = [{ fileName: fileNames }];
    }

    // Updating deleted files list in local storage
    this.storageService.addDeletedFileUrlsToStorage(deletedFileUrlsList);
  }

  duplicateDefect(index: number, list: any[], listName: string): void {
    // INFO: Used in template
    const duplicateObject = this.getDeepCopy(list[index]);

    /* // INFO: Commenting as per RINA-6309
    // Updating fotoCount value in existing images
    let fotoCount = this.model.fotoCount; */
    const defectsList: any[] =
      duplicateObject.modelstructure['Scheda Ispezione Ponti di Livello 1'][
        'Scheda Ispezione Ponti di Livello 1 List1'
      ];

    defectsList.forEach((defectRow, defectRowIndex) => {
      // INFO: Similar functionality is also used in duplicateIndexedDbFiles()
      if (!defectRow['L']) return;

      // If defect has images
      const lValues = Object.keys(defectRow['L']);
      lValues.forEach((sezioneValue) => {
        if (!defectRow['L'][sezioneValue]['N° foto']) return;

        const updatedNFoto = '';
        /* // INFO: Commenting as per RINA-6309
        const nFotoValues = Object.entries(
          defectRow['L'][sezioneValue]['N° foto']
        );
        nFotoValues.forEach(([fotoKey, foto]) => {
          const splitArray = fotoKey.split(' ');
          fotoCount++;
          splitArray[1] = fotoCount;
          fotoKey = splitArray.join(' ');
          updatedNFoto[fotoKey] = foto;
        }); */

        // Updating N° foto
        duplicateObject.modelstructure['Scheda Ispezione Ponti di Livello 1'][
          'Scheda Ispezione Ponti di Livello 1 List1'
        ][defectRowIndex]['L'][sezioneValue]['N° foto'] = updatedNFoto;
      });
    });

    /* // INFO: Commenting as per RINA-6309
    this.inspectionService.setImageCount$(fotoCount); */
    this.model[TunnelConstants.levels.l2][listName] = list.concat([
      duplicateObject,
    ]);

    /* // INFO: Commenting as per RINA-6309
    this.duplicateIndexedDbFiles(
      this.model[TunnelConstants.levels.l2][listName].length - 1,
      index,
      true,
      listName,
      duplicateObject.modelstructure[TunnelConstants.partNo]
    ); */

    this.refreshFields();
  }

  getConfirmation(dialogData?: ConfirmationDialogData): Promise<boolean> {
    // INFO: Used in template
    return this.cubeService.confirm(dialogData).toPromise();
  }

  getDeepCopy(item: any): any {
    // INFO: Used in template
    return this.cubeService.getDeepCopy(item);
  }

  getDefectsList(level: string): void {
    // INFO: Used in template
    if (this.isOnline) {
      this.fetchDefectsList(level);
    } else {
      this.fetchDefectsListFromIndexedDb(level);
    }
  }

  isFieldDisabled(
    rolesList: UserRole[],
    isDisabled: boolean,
    isNotAllowed: boolean = false
  ): boolean {
    // INFO: Used in template
    if (isDisabled) return true;

    const isUserAllowedByRole = rolesList.length
      ? this.userService.isUserAllowedByRole(rolesList)
      : true;

    return isNotAllowed ? isUserAllowedByRole : !isUserAllowedByRole;
  }

  migrateDataToChildLevel(
    index: number,
    destinationLevel: string,
    sourceList
  ): void {
    // INFO: Used in template
    switch (destinationLevel) {
      case TunnelConstants.levels.l1:
        this.concioCount = sourceList[0].modelstructure.conciolist.length;
        this.saveDeletedFilesToCurrentLevel(
          this.model[TunnelConstants.levels.l0]
        );
        this.l0BackupData = this.getDeepCopy(
          this.model[TunnelConstants.levels.l0]
        );
        this.l1UpdateStatus = true;
        this.l1UpdateIndex = index;

        break;

      case TunnelConstants.levels.l2:
        this.saveDeletedFilesToCurrentLevel(
          this.model[TunnelConstants.levels.l1]
        );
        this.l1BackupData = this.getDeepCopy(
          this.model[TunnelConstants.levels.l1]
        );
        this.l2UpdateStatus = true;
        this.l2UpdateIndex = index;
        this.l2ConcioNo = sourceList[index][TunnelConstants.partNo];

        break;

      default:
        this.defectUpdateStatus = true;
        this.defectUpdateIndex = index;
        this.inspectionService.setDefectRowIndex$(index);

        /* // Functionality now handled by syncSharedModelDataForList(). The following code can be removed at a later stage.
        // TODO will need migration to update existing inspections
        // Setting Codice IOP for defect levels
        for (const source of sourceList) {
          source.modelstructure['Scheda Ispezione Ponti di Livello 1'][
            'Codice IOP'
          ] = this.model[TunnelConstants.levels.l0]['Codice IOP'];
        } */
        this.saveDeletedFilesToCurrentLevel(
          this.model[TunnelConstants.levels.l2]
        );

        break;
    }

    this.model[destinationLevel] ??= {};

    this.form = new FormGroup({});
    this.model[destinationLevel] = this.getDeepCopy(
      sourceList[index].modelstructure
    );
    this.options.formState[destinationLevel] = false;
  }

  onClickBack(): void {
    this.cubeService
      .confirm({
        message: `templates.${
          this.model.activeLevel === TunnelConstants.levels.l0
            ? 'go_back_home_page'
            : 'unsaved_progress'
        }`,
      })
      .pipe(
        filter((isConfirmed) => isConfirmed),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.goBack();
      });
  }

  async onClickSave(): Promise<void> {
    let isValid: boolean;
    switch (this.model.activeLevel) {
      case TunnelConstants.levels.l0: // Level 0
        this.saveModelToIndexedDb();

        break;

      case TunnelConstants.levels.l1: // Level 1
        this.migrateDataFromLevel1ToLevel0();

        break;

      case TunnelConstants.levels.l2: // Level 2
        isValid = await this.validateConcioNo();
        if (isValid) this.migrateDataFromLevel2ToLevel1();

        break;

      case TunnelConstants.levels.defect1: // Defects Template - 1 - Difetti relativi alla struttura
      case TunnelConstants.levels.defect2: // Defects Template - 2 - Difetti relativi alla struttura
      case TunnelConstants.levels.defect3: // Defects Template - 3 - Difetti relativi alla struttura
      case TunnelConstants.levels.defect4: // Defects Template - Difetti relativi alla piattaforma stradale/autostradale
      case TunnelConstants.levels.defect5: // Defects Template - Difetti relativi agli elementi non strutturali e impianti
        const defectLevel = this.model.activeLevel;
        this.migrateDataFromDefectLevelsToLevel2(
          defectLevel,
          `${defectLevel}list`
        );

        break;
    }
  }

  refreshFields(): void {
    // INFO: Used in template
    this.fields = [...this.fields];
  }

  saveModelToIndexedDbForPlannedInspection(): void {
    // INFO: Used in template
    if (
      this.inspectionType !== InspectionType.plannedInspection &&
      this.inspectionType !== InspectionType.submittedInspection
    ) {
      this.loaderService.requestEnded();
      return;
    }

    // Checking if model already exists in indexedDB
    this.idbService.getInspectionModelsList().then((res) => {
      // Below condition is only applicable in a single scenario where the user manually refreshes the app or click browser back button which leads to running outdated url information from inspection page.
      // In that case the planned/submitted inspection is already added to the indexedDB by the setTakeInChargeStatus() function.
      // Below condition then will find the corresponding model from indexedDB and will update it rather than creating a duplicate entity
      if (res.length) {
        for (let i = 0; i < res.length; i++) {
          const modelData = JSON.parse(res[i].model);
          // Inspection data already stored in indexedDB

          // For planned inspection we are checking planned inspection id, since the inspection is new and not yet have an inspection id.
          // For submitted / massive imported inspection we are checking the inspection id since it is an existing inspection
          // For app imported inspection, it is referring to an existing inspection since it doesnt have inspection id generate yet, so we use the referencing inspection id for check
          let checkId;
          switch (this.inspectionType) {
            case InspectionType.plannedInspection:
              checkId = modelData.plannedInspectionId;
              break;

            case InspectionType.appImportedInspection:
              checkId = modelData.appImportedInspectionId;
              break;

            default:
              checkId = modelData.id;
              break;
          }

          if (checkId === this.recordId) {
            this.idbRecordId = res[i].id;
            this.inspectionType = InspectionType.localInspection;
            this.model = modelData;
            this.loaderService.requestEnded();

            return;
          }
        }
      }

      // Inspection is started for the first time
      this.setTakeInChargeStatus();
    });
  }

  setImages(level: string): void {
    // INFO: Used in template
    const savedImagesList = this.storageService.getSavedFilesListFromStorage();

    switch (level) {
      case TunnelConstants.levels.l0:
        this.model.sharedData.l0Images =
          this.model.sharedData.l0Images.concat(savedImagesList);

        break;

      case TunnelConstants.levels.l1:
        this.model.sharedData.l1Images =
          this.model.sharedData.l1Images.concat(savedImagesList);

        break;

      case TunnelConstants.levels.l2:
        this.model.sharedData.l2Images =
          this.model.sharedData.l2Images.concat(savedImagesList);

        break;
    }

    this.storageService.clearFilesListInStorage(STORAGE_KEYS.savedImages);
  }

  syncSharedModelData(
    pathsKey: string,
    updatedValue: any,
    changedIndex?: number
  ): void {
    // INFO: Used in template
    const propPaths = this.model.sharedData[pathsKey];

    propPaths.forEach(({ path, syncCondition }) => {
      const shouldSync = syncCondition ? eval(syncCondition) : true;
      if (!shouldSync) return;

      const pathValue =
        typeof path === 'string' ? eval(path)(changedIndex) : path;
      const pathCopy = [...pathValue];
      const key = pathCopy.pop();
      const parent = this.cubeService.getPropByPathList(this.model, pathCopy);

      if (parent != null && parent[key] != updatedValue) {
        parent[key] = updatedValue;
      }
    });

    this.refreshFields();
  }

  syncSharedModelDataForList(pathsKey: string, updatedValue: any): void {
    // INFO: Used in template
    const propPaths = this.getDeepCopy(this.model.sharedDataForList[pathsKey]);
    propPaths.forEach(({ path, list, subList, syncCondition }) => {
      const shouldSync = syncCondition ? eval(syncCondition) : true;
      if (!shouldSync) return;
      const items = this.cubeService.getPropByPathList(this.model, list);

      items?.forEach((item) => {
        if (subList == null) {
          this.updateSharedValues(updatedValue, item, path);
        } else {
          const subItems = this.cubeService.getPropByPathList(item, subList);

          subItems?.forEach((subItem) => {
            this.updateSharedValues(updatedValue, subItem, path);
          });
        }
      });
    });

    this.refreshFields();
  }

  private async clearDeletedFilesFromCurrentLevelAndStorage(
    currentModel: any,
    level?: string
  ): Promise<void> {
    delete currentModel.deletedFiles;

    // NEW NOTES: here the deleted images list was also getting concatted with saved image
    // and then getting deleted from indexdb, but this function is called when back button is
    // clicked meaning deleted images in that case should not be deleted
    // Fetching deleted images
    // const deletedImages = this.storageService.getDeletedFilesListFromStorage();

    // Fetching added images
    let savedFiles = this.storageService
      .getSavedFilesListFromStorage()
      .map((fileURL: string) => ({
        fileName: fileURL,
        isDefect: this.isDefectFile(fileURL),
      }));

    // Concatenate deleted images and added images to prepare for the removal of these files from the indexedDB.
    /*savedFiles = savedFiles.concat(
      this.model.sharedData.l0Images.map((fileURL: string) => ({
        fileName: fileURL,
        isDefect: this.isDefectFile(fileURL),
      }))
    );*/

    const tempTableFiles = await this.getFilesFromTempTable();
    const tempTableKeys = [];

    for (let file of savedFiles) {
      // If deleted file is in indexedDB, first find whether the image is in temp table or original table
      // then deleting file from corresponding indexedDB table
      const fileInTempTable = tempTableFiles.find(
        (tempFile) => tempFile.fileName === file.fileName
      );
      if (fileInTempTable.length && !fileInTempTable.existingImageStatus) {
        tempTableKeys.push(fileInTempTable.id);
      } else {
        await this.idbService.deleteUploadedFile(
          file,
          { modelHash: this.model.modelHash },
          file.isDefect ? StructureType.tunnel : null
        );
      }
    }

    if (tempTableKeys.length)
      await this.idbService.deleteUploadedFilesFromTempTable(tempTableKeys);

    this.storageService.clearFilesListInStorage(STORAGE_KEYS.savedImages);
    this.storageService.clearFilesListInStorage(STORAGE_KEYS.deletedFiles);

    if (level === TunnelConstants.levels.l0) {
      // Case only happens when user clicks back instead of save as draft in L0. Therefore clearing images in temp table to avoid file residue persist
      this.idbService.clearTempTable(this.model.modelHash);
    } else {
      this.setImagesBackToSavedList(level);
    }
  }

  private createFormlyOptions(): FormlyFormOptions {
    return {
      formState: {
        [TunnelConstants.levels.defect1]: true,
        [TunnelConstants.levels.defect2]: true,
        [TunnelConstants.levels.defect3]: true,
        [TunnelConstants.levels.defect4]: true,
        [TunnelConstants.levels.defect5]: true,
      },
    };
  }

  private createLevelsList(): string[] {
    const levelsList = Object.values(TunnelConstants.levels);

    return [...levelsList];
  }

  private createModel(): any {
    return {
      activeLevel: TunnelConstants.levels.l0,
      fotoCount: 0,
      [TunnelConstants.levels.l0]: {},
      [TunnelConstants.levels.l1]: {},
      modelHash: this.cubeService.generateUniqueId(),
      modelId: '',
      status: InspectionStatus.draft,
      structure_type: StructureType.tunnel,
      templateId: `${StructureType.tunnel}_${this.templateVersion}`,
    };
  }

  private async deleteUnusedImages(): Promise<void> {
    const tempTableFiles = await this.getFilesFromTempTable();
    const tempTableKeys = [];

    let deletedFilesList = this.storageService.getDeletedFilesListFromStorage();

    deletedFilesList = deletedFilesList.concat(
      this.model[TunnelConstants.levels.l0].deletedFiles ?? []
    );

    const alreadyUploadedFilesList = [];
    deletedFilesList.forEach((deletedFile) => {
      // If deleted file was already uploaded
      if (this.cubeService.isAlreadyUploadedFile(deletedFile.fileName)) {
        alreadyUploadedFilesList.push(deletedFile.fileName);

        return;
      }

      // If deleted file is in indexedDB, first find whether the image is in temp table or original table
      // then deleting file from corresponding indexedDB table
      const fileInTempTable = tempTableFiles.find(
        (file) => file.fileName === deletedFile.fileName
      );
      if (fileInTempTable && !fileInTempTable.existingImageStatus) {
        tempTableKeys.push(fileInTempTable.id);
      } else {
        this.idbService.deleteUploadedFile(
          deletedFile,
          { modelHash: this.model.modelHash },
          deletedFile.isDefect ? StructureType.tunnel : null
        );
      }

      const fileAlreadyInListIndex =
        this.repeatSectionImageListForModification.findIndex(
          ({ fileName }) => fileName === deletedFile.fileName
        );
      const defectFileAlreadyInListIndex =
        this.defectImageListForModification.findIndex(
          ({ fileName }) => fileName === deletedFile.fileName
        );
      if (fileAlreadyInListIndex !== -1)
        this.repeatSectionImageListForModification.splice(
          fileAlreadyInListIndex,
          1
        );

      if (defectFileAlreadyInListIndex !== -1)
        this.defectImageListForModification.splice(
          defectFileAlreadyInListIndex,
          1
        );
    });

    if (tempTableKeys.length)
      await this.idbService.deleteUploadedFilesFromTempTable(tempTableKeys);

    if (alreadyUploadedFilesList.length) {
      // Deleting files from DB
      await this.inspectionService
        .deleteUploadedFiles(alreadyUploadedFilesList)
        .toPromise();
    }

    this.storageService.clearFilesListInStorage(STORAGE_KEYS.deletedFiles);
    this.storageService.clearFilesListInStorage(STORAGE_KEYS.savedImages);
    delete this.model[TunnelConstants.levels.l0].deletedFiles;
  }

  /* // INFO: Commenting as per RINA-6309
  private async duplicateIndexedDbFiles(
    newValue: number,
    currentValue: number,
    isDuplicateDefect: boolean,
    listName: string = '',
    concioNo: number = 0
  ) {
    let whereConditions: WhereConditions = {
      campateNo: currentValue,
      modelHash: this.model.modelHash,
    };
    if (isDuplicateDefect) {
      whereConditions = {
        ...whereConditions,
        campateNo: concioNo,
        casoType: -1,
        componentListName: listName,
        componentRowIndex: currentValue,
      };
    }

    const defectFilesList = await this.idbService.getUploadedFiles(
      whereConditions,
      StructureType.tunnel,
    );
    let fotoCount = this.model.fotoCount - defectFilesList.length;
    const newList = defectFilesList.map((listitem) => {
      // INFO: Similar functionality is also used in duplicateDefect()
      if (isDuplicateDefect) {
        listitem.componentRowIndex = newValue;
      } else {
        listitem.campateNo = newValue;
      }

      delete listitem.id;
      const nameParts = listitem.fotoName.split(' ');
      fotoCount++;
      nameParts[1] = fotoCount;
      listitem.fotoName = nameParts.join(' ');

      return listitem;
    });

    await this.idbService.addUploadedFiles(newList);
  } */

  private fetchDefectsList(level: string): void {
    this.inspectionService
      .getTunnelDefects(level)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.setDefects(res.data, level);
        },
        (error) => {
          this.fetchDefectsListFromIndexedDb(level);
        }
      );
  }

  private async fetchDefectsListFromIndexedDb(level: string): Promise<void> {
    const defectsList = await this.idbService.getTunnelDefectsList(level);
    this.setDefects(defectsList, level);
  }

  private generateConcioNoList(): void {
    const concioNo =
      this.model[TunnelConstants.levels.l0][
        'Tipologia e caratteristiche del rivestimento'
      ]['Rivestimento definitivo'][
        'In caso di utilizzo dii conci prefabbricati'
      ]['Numero di conci'];
    this.concioNoList = Array(concioNo)
      .fill(0)
      .map((x, i) => ({
        value: i + 1,
        label: i + 1,
      }));
  }

  private generateTemplate(): void {
    this.loaderService.requestStarted();
    switch (this.inspectionType) {
      case InspectionType.newInspection:
        this.getTemplateByVersion(this.templateVersion);

        return;

      case InspectionType.plannedInspection:
        this.plannedInspectionId = this.recordId;
        this.getPlannedInspection(this.plannedInspectionId);

        return;

      case InspectionType.appImportedInspection:
        this.appImportedInspectionId = this.recordId;
        this.importInspection(this.appImportedInspectionId);

        return;

      case InspectionType.localInspection:
        this.idbRecordId = this.recordId;
        from(this.idbService.getInspectionModel(parseInt(this.idbRecordId)))
          .pipe(takeUntil(this.destroy$))
          .subscribe((res) => {
            this.model = JSON.parse(res.model);
            this.setImagesToTempTable();
            this.getImageFromIndexedDb();
            this.generateConcioNoList();
          });

        return;

      case InspectionType.submittedInspection:
        this.inspectionId = this.recordId;
        this.getSubmittedInspection(this.recordId);

        return;

      default:
        break;
    }
  }

  private async getImageFromIndexedDb(): Promise<void> {
    const multiImagesList = await this.idbService.getUploadedFilesList({
      modelHash: this.model.modelHash,
      imageType: 2,
    });
    const fileList = multiImagesList.map((image) => image.file);

    this.model[TunnelConstants.levels.l0].MediaList.mediaFile = fileList;
    this.getTemplateByVersion(this.templateVersion);
  }

  private getInspectionMediaToken(): Promise<void> {
    return this.inspectionService
      .getInspectionMediaToken()
      .pipe(
        map((res) => {
          this.token = res.data.token;
          this.storageService.addToStorage(
            STORAGE_KEYS.inspectionToken,
            res.data.token
          );
        }),
        takeUntil(this.destroy$)
      )
      .toPromise();
  }

  private getPlannedInspection(plannedInspectionId: string): void {
    this.inspectionCrudService
      .getPlannedInspection(plannedInspectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.plannedInspection = res.data;
        this.model.plannedInspectionId = this.plannedInspectionId;
        this.getTemplateByVersion(this.templateVersion);
      });
  }

  private getSubmittedInspection(inspectionId: string): void {
    this.inspectionService
      .getInspection(inspectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.model = res.data;
        this.model.status = InspectionStatus.takeInCharge;
        this.getTemplateByVersion(this.templateVersion);
        this.generateConcioNoList();
      });
  }

  private async getTemplateByVersion(
    templateVersion: TunnelTemplateVersion
  ): Promise<void> {
    const template = await this.templateLoaderService.loadTunnelTemplate(
      templateVersion,
      {
        component: this,
        isDisabled: this.isDisabled,
        userRole: this.userService.getActiveRole(),
      }
    );
    this.setTemplate(template);
  }

  private async getFilesFromTempTable(
    additionalWhereConditions?: WhereConditions
  ): Promise<any[]> {
    const whereConditions: WhereConditions = {
      modelHash: this.model.modelHash,
      ...additionalWhereConditions,
    };
    const imageList = await this.idbService.getFilesFromTempTable(
      whereConditions
    );
    return imageList;
  }

  private goBack(): void {
    switch (this.model.activeLevel) {
      case TunnelConstants.levels.l0: // Level 0
        this.clearDeletedFilesFromCurrentLevelAndStorage(
          this.model[TunnelConstants.levels.l0],
          TunnelConstants.levels.l0
        );
        this.deleteUnusedImages();
        this.location.back();

        break;

      case TunnelConstants.levels.l1: // Level 1
        this.l1UpdateStatus = false;
        delete this.l1UpdateIndex;
        this.clearDeletedFilesFromCurrentLevelAndStorage(
          this.model[TunnelConstants.levels.l1],
          TunnelConstants.levels.l1
        );
        this.model[TunnelConstants.levels.l0] = this.l0BackupData;
        this.model[TunnelConstants.levels.l1] = this.l1InitBackupData;
        this.model.activeLevel = TunnelConstants.levels.l0;
        this.refreshFields();

        break;

      case TunnelConstants.levels.l2: // Level 2
        this.l2UpdateStatus = false;
        delete this.l2UpdateIndex;
        delete this.l2ConcioNo;
        this.clearDeletedFilesFromCurrentLevelAndStorage(
          this.model[TunnelConstants.levels.l2],
          TunnelConstants.levels.l2
        );
        this.model[TunnelConstants.levels.l1] = this.l1BackupData;
        this.resetFields();
        this.options.formState[TunnelConstants.levels.l2] = true;
        this.model.activeLevel = TunnelConstants.levels.l1;
        this.refreshFields();

        break;

      case TunnelConstants.levels.defect1: // Defects Template 1 - Difetti relativi alla struttura
      case TunnelConstants.levels.defect2: // Defects Template 2 - Difetti relativi alla struttura
      case TunnelConstants.levels.defect3: // Defects Template 3 - Difetti relativi alla struttura
      case TunnelConstants.levels.defect4: // Defects Template 4 - Difetti relativi alla piattaforma stradale/autostradale
      case TunnelConstants.levels.defect5: // Defects Template 5 - Difetti relativi agli elementi non strutturali e impianti
        this.defectUpdateStatus = false;
        delete this.defectUpdateIndex;
        this.clearDeletedFilesFromCurrentLevelAndStorage(
          this.model[this.model.activeLevel],
          this.model.activeLevel
        );
        this.options.formState[this.model.activeLevel] = true;
        this.refreshFields();
        this.model.activeLevel = TunnelConstants.levels.l2;

        break;
    }
  }

  private importInspection(appImportedInspectionId: string): void {
    this.inspectionService
      .getInspection(appImportedInspectionId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.model = res.data;
        this.model.appImportedInspectionId = this.appImportedInspectionId;
        this.updateModelForImportInspection();
        this.getTemplateByVersion(this.templateVersion);
      });
  }

  private isDefectFile(fileName: string): boolean {
    const fileNameSplit = fileName.split(' ');

    return fileNameSplit.length >= 2 &&
      fileNameSplit[0] === 'Foto' &&
      Number(fileNameSplit[1])
      ? true
      : false;
  }

  private migrateDataFromDefectLevelsToLevel2(
    level: string,
    defectList: string
  ): void {
    const modelValue = this.getDeepCopy(this.model[level]);

    if (
      !this.devMode.disableDefectsValidation && // TEMP: Disable defect validations
      !this.validateDefectsTableData(
        modelValue['Scheda Ispezione Ponti di Livello 1'][
          'Scheda Ispezione Ponti di Livello 1 List1'
        ]
      )
    ) {
      this.cubeService.confirm({
        isCancelButtonVisible: false,
        message: 'templates.update_all_defects',
        title: 'confirmation_dialog.title.info',
      });

      return;
    }

    this.loaderService.requestStarted();
    this.model.activeLevel = TunnelConstants.levels.l2;

    setTimeout(() => {
      // Fetching current child's images and storing into the child's parent
      const savedImagesList =
        this.storageService.getSavedFilesListFromStorage();
      this.model.sharedData.l2Images =
        this.model.sharedData.l2Images.concat(savedImagesList);

      this.migrateDeletedFilesFromChildToParentLevel(
        TunnelConstants.levels.l2,
        modelValue
      );

      this.setImagesBackToSavedList(level);

      if (this.defectUpdateStatus) {
        // Defect edit
        this.model[TunnelConstants.levels.l2][defectList][
          this.defectUpdateIndex
        ] = {
          [TunnelConstants.partNo]:
            this.model[TunnelConstants.levels.l2][TunnelConstants.partNo],
          modelstructure: modelValue,
        };

        this.model[TunnelConstants.levels.l2][defectList] = this.getDeepCopy(
          this.model[TunnelConstants.levels.l2][defectList]
        );
        delete this.defectUpdateIndex;
        this.defectUpdateStatus = false;
      } else {
        // Defect add
        this.model[TunnelConstants.levels.l2][defectList] = this.model[
          TunnelConstants.levels.l2
        ][defectList].concat({
          [TunnelConstants.partNo]:
            this.model[TunnelConstants.levels.l2][TunnelConstants.partNo],
          modelstructure: modelValue,
        });
      }

      this.options.formState[level] = true;
      this.refreshFields();
      this.loaderService.requestEnded();
    }, this.migrationDelay);
  }

  private migrateDataFromLevel1ToLevel0(): void {
    const modelValue = this.getDeepCopy(this.model[TunnelConstants.levels.l1]);

    this.loaderService.requestStarted();
    this.model.activeLevel = TunnelConstants.levels.l0;

    let defect1 = 0;
    let defect2 = 0;
    let defect3 = 0;
    let defect4 = 0;
    let defect5 = 0;

    for (const concio of modelValue.conciolist) {
      defect1 +=
        concio.modelstructure[`${TunnelConstants.levels.defect1}list`].length;
      defect2 +=
        concio.modelstructure[`${TunnelConstants.levels.defect2}list`].length;
      defect3 +=
        concio.modelstructure[`${TunnelConstants.levels.defect3}list`].length;
      defect4 +=
        concio.modelstructure[`${TunnelConstants.levels.defect4}list`].length;
      defect5 +=
        concio.modelstructure[`${TunnelConstants.levels.defect5}list`].length;
    }

    setTimeout(() => {
      // Fetching current child's images and storing into the child's parent
      const savedImagesList =
        this.storageService.getSavedFilesListFromStorage();
      this.model.sharedData.l0Images =
        this.model.sharedData.l0Images.concat(savedImagesList);

      this.migrateDeletedFilesFromChildToParentLevel(
        TunnelConstants.levels.l0,
        modelValue
      );

      this.setImagesBackToSavedList(TunnelConstants.levels.l1);

      if (this.l1UpdateStatus) {
        // Level 1 edit
        this.model[TunnelConstants.levels.l0].opereparzialilist[
          this.l1UpdateIndex
        ] = {
          'N° Concio': modelValue.conciolist.length,
          'N° 1 - Difetti relativi alla struttura': defect1,
          'N° 2 - Difetti relativi alla struttura': defect2,
          'N° 3 - Difetti relativi alla struttura': defect3,
          'N° Difetti relativi alla piattaforma stradale/autostradale': defect4,
          'N° Difetti relativi agli elementi non strutturali e impianti':
            defect5,
          modelstructure: modelValue,
        };
        this.model[TunnelConstants.levels.l0].opereparzialilist =
          this.getDeepCopy(
            this.model[TunnelConstants.levels.l0].opereparzialilist
          );
        this.l1UpdateStatus = false;
        delete this.l1UpdateIndex;
      } else {
        // Level 1 add
        this.model[TunnelConstants.levels.l0].opereparzialilist = this.model[
          TunnelConstants.levels.l0
        ].opereparzialilist.concat({
          'N° Concio': modelValue.conciolist?.length ?? 0,
          'N° 1 - Difetti relativi alla struttura': defect1,
          'N° 2 - Difetti relativi alla struttura': defect2,
          'N° 3 - Difetti relativi alla struttura': defect3,
          'N° Difetti relativi alla piattaforma stradale/autostradale': defect4,
          'N° Difetti relativi agli elementi non strutturali e impianti':
            defect5,
          modelstructure: modelValue,
        });
      }

      this.options.formState[TunnelConstants.levels.l1] = true;
      this.refreshFields();
      this.loaderService.requestEnded();
    }, this.migrationDelay);
  }

  private migrateDataFromLevel2ToLevel1(): void {
    const modelValue = this.getDeepCopy(this.model[TunnelConstants.levels.l2]);

    this.loaderService.requestStarted();
    this.model.activeLevel = TunnelConstants.levels.l1;

    setTimeout(() => {
      // Fetching current child's images and storing into the child's parent
      const savedImagesList =
        this.storageService.getSavedFilesListFromStorage();
      this.model.sharedData.l1Images =
        this.model.sharedData.l1Images.concat(savedImagesList);

      this.migrateDeletedFilesFromChildToParentLevel(
        TunnelConstants.levels.l1,
        modelValue
      );

      this.setImagesBackToSavedList(TunnelConstants.levels.l2);

      if (this.l2UpdateStatus) {
        // Level 2 edit
        this.model[TunnelConstants.levels.l1].conciolist[this.l2UpdateIndex] = {
          [TunnelConstants.partNo]: modelValue[TunnelConstants.partNo],
          modelstructure: modelValue,
        };
        this.model[TunnelConstants.levels.l1].conciolist = this.getDeepCopy(
          this.model[TunnelConstants.levels.l1].conciolist
        );

        this.l2UpdateStatus = false;
        delete this.l2UpdateIndex;
        delete this.l2ConcioNo;
      } else {
        // Level 2 add
        this.model[TunnelConstants.levels.l1].conciolist = this.model[
          TunnelConstants.levels.l1
        ].conciolist.concat({
          [TunnelConstants.partNo]: modelValue[TunnelConstants.partNo],
          modelstructure: modelValue,
        });
      }

      this.model[TunnelConstants.levels.l2] = {
        [`${TunnelConstants.levels.defect1}list`]: [],
        [`${TunnelConstants.levels.defect2}list`]: [],
        [`${TunnelConstants.levels.defect3}list`]: [],
        [`${TunnelConstants.levels.defect4}list`]: [],
        [`${TunnelConstants.levels.defect5}list`]: [],
      };

      this.options.formState[TunnelConstants.levels.l2] = true;
      this.refreshFields();
      this.loaderService.requestEnded();
    }, this.migrationDelay);
  }

  private migrateDeletedFilesFromChildToParentLevel(
    targetLevel: string,
    currentModel: any
  ): void {
    // Fetch deleted files from current level
    const newlyDeletedFiles =
      this.storageService.getDeletedFilesListFromStorage();
    // Fetch deleted files already present in level
    const currentDeletedFiles = currentModel.deletedFiles ?? [];
    delete currentModel.deletedFiles;

    // Migrate deleted files to parent from child
    const targetModel = this.model[targetLevel];
    targetModel.deletedFiles ??= [];
    targetModel.deletedFiles = targetModel.deletedFiles.concat([
      ...currentDeletedFiles,
      ...newlyDeletedFiles,
    ]);

    this.storageService.clearFilesListInStorage(STORAGE_KEYS.deletedFiles);
    this.storageService.clearFilesListInStorage(STORAGE_KEYS.savedImages);
  }

  private resetFields(): void {
    this.model[TunnelConstants.levels.l2] = {
      [`${TunnelConstants.levels.defect1}list`]: [],
      [`${TunnelConstants.levels.defect2}list`]: [],
      [`${TunnelConstants.levels.defect3}list`]: [],
      [`${TunnelConstants.levels.defect4}list`]: [],
      [`${TunnelConstants.levels.defect5}list`]: [],
    };
  }

  private saveDeletedFilesToCurrentLevel(currentModel: any): void {
    // Fetch deleted files from current level
    const newlyDeletedFiles =
      this.storageService.getDeletedFilesListFromStorage();

    // Migrate deleted files to current level
    currentModel.deletedFiles ??= [];
    currentModel.deletedFiles = currentModel.deletedFiles.concat([
      ...newlyDeletedFiles,
    ]);

    this.storageService.clearFilesListInStorage(STORAGE_KEYS.deletedFiles);
  }

  private async saveImagesToTable(): Promise<void> {
    const imageList = await this.getFilesFromTempTable();
    let inspectionFiles = [];
    let defectFiles = [];
    let keys = [];
    for (const image of imageList) {
      keys.push(image.id);
      if (!image.existingImageStatus) {
        if (this.isDefectFile(image.fileName)) defectFiles.push(image.details);
        else inspectionFiles.push(image.details);
      }
    }
    if (inspectionFiles.length)
      await this.idbService.addUploadedFiles(inspectionFiles);
    if (defectFiles.length)
      await this.idbService.addUploadedFiles(defectFiles, StructureType.tunnel);
    await this.idbService.deleteUploadedFilesFromTempTable(keys);
  }

  private async saveModelToIndexedDb(): Promise<void> {
    // Deleting unused data from model
    this.model.sharedData.l0Images = [];
    this.model.sharedData.l1Images = [];
    this.model.sharedData.l2Images = [];
    this.model.sharedData.l3Images = [];

    if (!this.model[TunnelConstants.levels.l0]['Codice IOP']) {
      this.cubeService.confirm({
        isCancelButtonVisible: false,
        message: 'templates.codice_iop_required',
        title: 'confirmation_dialog.title.error',
      });

      return;
    }

    await this.deleteUnusedImages();
    await this.saveImagesToTable();
    this.updateRepeatSectionImageIndex();
    this.updateDefectImageIndex();

    if (this.inspectionType === InspectionType.localInspection) {
      this.idbService.updateInspectionModel(
        parseInt(this.idbRecordId),
        JSON.stringify(this.model)
      );
    } else {
      this.idbService.addInspectionModel(this.model);
    }

    this.saveStatus = true;
    this.router.navigate(['/bms-app/to-do']);
    this.cubeService.showMessage(
      MessageType.Info,
      'templates.inspection_save_success'
    );
  }

  private setDefects(defectsList: any[], level: string): void {
    if (this.defectUpdateStatus) return;

    this.inspectionService.setDefectRowIndex$(
      this.model[TunnelConstants.levels.l2][`${level}list`].length
    );

    this.model[level]['Scheda Ispezione Ponti di Livello 1'][
      'Scheda Ispezione Ponti di Livello 1 List1'
    ] = defectsList;

    this.options.formState[level] = false;

    this.refreshFields();
  }

  private setImageCountEventListener(): void {
    this.inspectionService
      .getImageCount$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((imageCount) => {
        this.model.fotoCount = imageCount;
      });
  }

  // Moving saved images of parent back to savedImageList in storage from model
  private setImagesBackToSavedList(level: string): void {
    switch (level) {
      case TunnelConstants.levels.l1:
        // Moving saved L0 images in level to savedImagesList
        this.storageService.addSavedFileUrlsToStorage(
          this.model.sharedData.l0Images
        );
        this.model.sharedData.l0Images = [];
        this.model.sharedData.l1Images = [];
        break;

      case TunnelConstants.levels.l2:
        // Moving saved L1 images in level to savedImagesList
        this.storageService.addSavedFileUrlsToStorage(
          this.model.sharedData.l1Images
        );
        this.model.sharedData.l1Images = [];
        this.model.sharedData.l2Images = [];
        break;

      case TunnelConstants.levels.defect1: // Defects Template - Defect 1
      case TunnelConstants.levels.defect2: // Defects Template - Defect 2
      case TunnelConstants.levels.defect3: // Defects Template - Defect 3
      case TunnelConstants.levels.defect4: // Defects Template - Defect 4
      case TunnelConstants.levels.defect5: // Defects Template - Defect 5
        // Moving saved L2 images in level to savedImagesList
        this.storageService.addSavedFileUrlsToStorage(
          this.model.sharedData.l2Images
        );
        this.model.sharedData.l2Images = [];
        break;
    }
  }

  private async setImagesToTempTable(): Promise<void> {
    await this.idbService.clearTempTable(this.model.modelHash);

    const imagesList = await this.idbService.getUploadedFilesList({
      modelHash: this.model.modelHash,
    });
    const defectImagesList = await this.idbService.getUploadedFilesList(
      {
        modelHash: this.model.modelHash,
      },
      StructureType.tunnel
    );

    let uploadedFilesList = [];
    uploadedFilesList = imagesList.map((image) => {
      return {
        originalKey: image.id,
        fileName: image.basePathName,
        modelHash: this.model.modelHash,
        existingImageStatus: true,
        details: null,
      };
    });
    uploadedFilesList = uploadedFilesList.concat(
      defectImagesList.map((image) => {
        return {
          originalKey: image.id,
          fileName: image.fotoName,
          modelHash: this.model.modelHash,
          existingImageStatus: true,
          details: null,
        };
      })
    );
    this.idbService.addUploadedFilesToTempTable(uploadedFilesList);
  }

  private setIsOnline(): Promise<void> {
    return this.inspectionService.getOnlineStatus().then((isOnline) => {
      this.cubeService.setOnlineStatus(isOnline);
      this.isOnline = isOnline;
    });
  }

  private setTakeInChargeStatus(): void {
    this.model.status = InspectionStatus.takeInCharge;

    let updateInspectionStatus$;
    if (this.inspectionType === InspectionType.plannedInspection) {
      const data = {
        'Inspection datas': JSON.stringify(this.model),
        planned_inspection_id: this.plannedInspectionId,
      };
      updateInspectionStatus$ =
        this.inspectionService.takeInChargeInspection(data);
    } else {
      const data = new FormData();
      data.append('data', JSON.stringify(this.model));
      updateInspectionStatus$ = this.inspectionService.updateInspectionStatus(
        this.inspectionId,
        data
      );
    }

    updateInspectionStatus$.pipe(takeUntil(this.destroy$)).subscribe(
      (res) => {
        this.model.id = res.data.id;
        this.idbService.addInspectionModel(this.model).then((id) => {
          this.idbRecordId = id;
        });
        this.inspectionType = InspectionType.localInspection;
        this.loaderService.requestEnded();
      },
      (err) => {
        this.idbService.getInspectionModelsList().then((res) => {
          this.idbService.deleteInspectionModel(res[res.length - 1].id);
          this.router.navigate(['bms-app/to-do/download']);
        });
        this.loaderService.requestEnded();
      }
    );
  }

  private setTemplate(template: any): void {
    // Getting dynamic model structure from template
    const dynamicModelStructure = template.shift();
    if (
      this.inspectionType === InspectionType.newInspection ||
      this.inspectionType === InspectionType.plannedInspection
    ) {
      // If new inspection, planned inspection or massiveImportedInspection, adding the dynamic model structure into the model
      this.model = {
        ...this.model,
        ...dynamicModelStructure.model,
      };
    }

    // Setting foto count in service to the foto count of this inspection
    this.inspectionService.setImageCount$(this.model.fotoCount);
    this.setImageCountEventListener();

    this.fields = template;
    this.updateModelForNewInspectionVariants();
  }

  private async updateDefectImageIndex(): Promise<void> {
    for (const file of this.defectImageListForModification) {
      await this.idbService.updateDefectFileIndex(
        {
          modelHash: this.model.modelHash,
          fotoName: file.fileName,
        },
        file.newIndex,
        StructureType.tunnel
      );
    }
  }

  private updateModelForImportInspection(): void {
    const updateModel = {
      activeLevel: TunnelConstants.levels.l0,
      created_at: '',
      created_by: '',
      id: '',
      modelHash: this.cubeService.generateUniqueId(),
      status: InspectionStatus.draft,
      updated_at: '',
      updated_by: '',
    };
    this.model = {
      ...this.model,
      ...updateModel,
    };
  }

  private updateModelForNewInspectionVariants(): void {
    if (
      this.inspectionType !== InspectionType.newInspection &&
      this.inspectionType !== InspectionType.plannedInspection
    ) {
      return;
    }

    this.model.templateId = '';
    this.model.tenantId = this.tenantId;

    if (this.inspectionType === InspectionType.newInspection) {
      // If new inspection
      this.model[TunnelConstants.levels.l0].Year = new Date().getFullYear();
    }

    // If planned inspection
    if (this.inspectionType === InspectionType.plannedInspection) {
      delete this.plannedInspection.id;
      this.model = {
        ...this.model,
        structure_type: this.plannedInspection.structure_type,
        [TunnelConstants.levels.l0]: {
          ...this.model[TunnelConstants.levels.l0],
          'Codice IOP': this.plannedInspection.stone_code,
          construction_site: this.plannedInspection.construction_site,
          detector: this.plannedInspection.detector,
          inspection_type: this.plannedInspection.inspection_type,
          'Nome Ponte Viadotto': this.plannedInspection.designation_of_the_work,
          planned_inspection_id: this.plannedInspection.id,
          quarter: this.plannedInspection.quarter,
          society: this.plannedInspection.society,
          special_means: this.plannedInspection.special_means,
          tenantId: this.plannedInspection.tenantId,
          'Tipo Opera': this.plannedInspection.type_of_work,
          type_of_work: this.plannedInspection.type_of_work,
          week: this.plannedInspection.week,
          Year: this.plannedInspection.year,
        },
        [TunnelConstants.levels.l1]: {
          ...this.model[TunnelConstants.levels.l1],
          'Codice IOP': this.plannedInspection.stone_code,
          // 'Nome Opera': this.plannedInspection.designation_of_the_work,
          'Nome Galleria/paramassi/sottopasso':
            this.plannedInspection.designation_of_the_work,
          'Scheda Ispezione Ponti di Livello 1': {
            ...this.model[TunnelConstants.levels.l1][
              'Scheda Ispezione Ponti di Livello 1'
            ],
            'Codice IOP': this.plannedInspection.stone_code,
          },
        },
        ...this.plannedInspection,
      };
    }
  }

  private updateSharedValues(
    updatedValue: any,
    data: any,
    path: string[]
  ): void {
    const pathDeepCopy = this.getDeepCopy(path);
    const pathValue = pathDeepCopy;
    const pathCopy = [...pathValue];
    const key = pathCopy.pop();
    const parent = this.cubeService.getPropByPathList(data, pathCopy);

    if (parent != null && parent[key] != updatedValue) {
      parent[key] = updatedValue;
    }
  }

  private async updateRepeatSectionImageIndex(): Promise<void> {
    for (const file of this.repeatSectionImageListForModification) {
      const idbFile = await this.idbService.getUploadedFilesList({
        modelHash: this.model.modelHash,
        basePathName: file.fileName,
      });
      const keyStructureLength = idbFile[0].keyStructure.length;
      idbFile[0].keyStructure[keyStructureLength - 2] =
        file.newIndex.toString();
      await this.idbService.updateRepeatSectionFileIndex(
        {
          modelHash: this.model.modelHash,
          basePathName: file.fileName,
        },
        idbFile[0].keyStructure
      );
    }
  }

  private async validateConcioNo(): Promise<boolean> {
    const selectedConcioNo =
      this.model[TunnelConstants.levels.l2][TunnelConstants.partNo];

    // If concio number is not selected
    if (!selectedConcioNo) {
      await this.getConfirmation({
        isCancelButtonVisible: false,
        message: 'templates.add_concio_no',
        title: 'confirmation_dialog.title.info',
      });

      return false;
    }

    // TODO is this needed ???
    // If selected concio number is already used
    const isConcioNoUsed = this.model[
      TunnelConstants.levels.l1
    ].conciolist?.some(
      (concio) => concio[TunnelConstants.partNo] === selectedConcioNo
    );
    if (this.l2ConcioNo !== selectedConcioNo && isConcioNoUsed) {
      await this.getConfirmation({
        isCancelButtonVisible: false,
        message: 'templates.concio_no_already_added',
        title: 'confirmation_dialog.title.info',
      });

      return false;
    }

    if (!this.l2UpdateStatus) this.concioCount += 1;

    return true;
  }

  private validateDefectsTableData(tableData: any[]): boolean {
    return tableData.length ? tableData.every((data) => data.valorized) : false;
  }
}
